import paper from 'paper';
import opentype from 'opentype.js';
import {BASE_LASER_COLORS, MM_PER_UNIT, VECTOR_STROKE_WIDTH, VECTOR_STROKE_COLOR} from '@/constants/editor';



function bakeTransform(item) {
  item.applyMatrix = true;
  return item;
}


function flattenIfNeeded(item, flattenToleranceMm) {
  if (!flattenToleranceMm) {
    return item;
  }

  item.flatten(
    flattenToleranceMm / MM_PER_UNIT
  );

  return item;
}


function pxToMm(value) {
  return value * MM_PER_UNIT;
}

function formatNumber(value, digits = 5) {
  const rounded = Number(value.toFixed(digits));

  if (Object.is(rounded, -0)) {
    return '0';
  }

  return String(rounded);
}

function collectLeafItems(item, out) {
  if (!item || item.visible === false) {
    return;
  }

  if (
    item instanceof paper.Group ||
    item instanceof paper.Layer
  ) {
    (item.children || []).forEach((child) => {
      collectLeafItems(child, out);
    });

    return;
  }

  if (
    item instanceof paper.CompoundPath ||
    item instanceof paper.Path ||
    item instanceof paper.Shape ||
    item instanceof paper.PointText
  ) {
    out.push(item);
  }
}

function getSourceColor(item) {
  if (item.fillColor && item.fillColor.alpha > 0) {
    return item.fillColor;
  }
  if (item.strokeColor && item.strokeColor.alpha > 0) {
    return item.strokeColor;
  }
  return new paper.Color('#000000');
}

function paperColorToRgb255(paperColor) {
  return {
    r: Math.round((paperColor.red ?? 0) * 255),
    g: Math.round((paperColor.green ?? 0) * 255),
    b: Math.round((paperColor.blue ?? 0) * 255),
  };
}

function colorDistanceSq(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return dr * dr + dg * dg + db * db;
}

export function matchBaseLaserColor(paperColor) {
  const rgb = paperColorToRgb255(paperColor);

  let best = BASE_LASER_COLORS[0];
  let bestDist = Infinity;

  for (const candidate of BASE_LASER_COLORS) {
    const dist = colorDistanceSq(rgb, candidate);
    if (dist < bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }

  return best;
}

function isSimpleTriangle(item) {
  if (!item) {
    return false;
  }

  if (!(item instanceof paper.Path)) {
    return false;
  }

  if (!item.closed) {
    return false;
  }

  if (!item.segments || item.segments.length !== 3) {
    return false;
  }

  return item.segments.every((segment) => {
    return (
      segment.handleIn.isZero() &&
      segment.handleOut.isZero()
    );
  });
}

function pathToPolygon(path, { mode, fillHex }) {
  const points = path.segments
    .map((segment) => {
      const x = formatNumber(segment.point.x);
      const y = formatNumber(segment.point.y);

      return `${x},${y}`;
    })
    .join(' ');

  if (mode === 'vector') {
    return (
      `<polygon ` +
      `points="${points}" ` +
      `fill="none" ` +
      `stroke="${VECTOR_STROKE_COLOR}" ` +
      `stroke-width="${formatNumber(parseFloat(VECTOR_STROKE_WIDTH), 2)}" ` +
      `stroke-linejoin="miter" ` +
      `stroke-linecap="butt"/>`
    );
  }

  return (
    `<polygon ` +
    `points="${points}" ` +
    `fill="${fillHex}" ` +
    `stroke="none"/>`
  );
}
function pathToSvgD(path) {
  const segments = path.segments;

  if (!segments || segments.length === 0) {
    return '';
  }

  let d =
    `M${formatNumber(segments[0].point.x)},` +
    `${formatNumber(segments[0].point.y)}`;

  const count = segments.length;

  for (let i = 0; i < count - 1; i++) {
    const curr = segments[i];
    const next = segments[i + 1];

    const hasOutHandle =
      !curr.handleOut.isZero();

    const hasInHandle =
      !next.handleIn.isZero();

    if (!hasOutHandle && !hasInHandle) {
      d +=
        ` L${formatNumber(next.point.x)},` +
        `${formatNumber(next.point.y)}`;

      continue;
    }

    const c1 = curr.point.add(
      curr.handleOut
    );

    const c2 = next.point.add(
      next.handleIn
    );

    d +=
      ` C${formatNumber(c1.x)},${formatNumber(c1.y)}` +
      ` ${formatNumber(c2.x)},${formatNumber(c2.y)}` +
      ` ${formatNumber(next.point.x)},${formatNumber(next.point.y)}`;
  }

  if (path.closed && count > 1) {
    const last = segments[count - 1];
    const first = segments[0];

    const hasOutHandle =
      !last.handleOut.isZero();

    const hasInHandle =
      !first.handleIn.isZero();

    if (hasOutHandle || hasInHandle) {
      const c1 = last.point.add(
        last.handleOut
      );

      const c2 = first.point.add(
        first.handleIn
      );

      d +=
        ` C${formatNumber(c1.x)},${formatNumber(c1.y)}` +
        ` ${formatNumber(c2.x)},${formatNumber(c2.y)}` +
        ` ${formatNumber(first.point.x)},${formatNumber(first.point.y)}`;
    } else {
      d +=
        ` L${formatNumber(first.point.x)},` +
        `${formatNumber(first.point.y)}`;
    }

    d += ' Z';
  } else if (!path.closed && count > 1) {
    const last = segments[count - 1];
    const first = segments[0];
    if (last.point.getDistance(first.point) < 0.05) {
      d += ' Z';
    }
  }

  return d;
}

function pathToSvg(item, { mode, fillHex }) {
  if (item instanceof paper.Group || item instanceof paper.CompoundPath) {
    const parts = [];
    (item.children || []).forEach((child) => {
      const svg = pathToSvg(child, { mode, fillHex });
      if (svg) {
        parts.push(svg);
      }
    });
    return parts.join('\n');
  }

  const d = pathToSvgD(item);

  if (!d) {
    return '';
  }

  if (mode === 'vector') {
    return (
      `<path ` +
      `d="${d}" ` +
      `fill="none" ` +
      `stroke="${VECTOR_STROKE_COLOR}" ` +
      `stroke-width="${formatNumber(parseFloat(VECTOR_STROKE_WIDTH), 2)}" ` +
      `stroke-linejoin="miter" ` +
      `stroke-linecap="butt"/>`
    );
  }

  return (
    `<path ` +
    `d="${d}" ` +
    `fill="${fillHex}" ` +
    `stroke="none" ` +
    `fill-rule="${item.fillRule === 'evenodd' ? 'evenodd' : 'nonzero'}"/>`
  );
}

function isBoldFontWeight(fontWeight) {
  if (
    fontWeight === 'bold' ||
    fontWeight === '700'
  ) {
    return true;
  }

  if (
    typeof fontWeight === 'number' &&
    fontWeight >= 700
  ) {
    return true;
  }

  if (
    typeof fontWeight === 'string' &&
    /^\d+$/.test(fontWeight)
  ) {
    return Number(fontWeight) >= 700;
  }

  return false;
}

function getFontPath(
  fontFamily,
  fontWeight,
  fontStyle,
  isItalicData
) {
  const normalizedFamily =
    String(fontFamily || '')
      .trim()
      .toLowerCase();

  const isBold =
    isBoldFontWeight(fontWeight);

  const isItalic =
    fontStyle === 'italic' ||
    fontStyle === 'oblique' ||
    Boolean(isItalicData);

  if (
    normalizedFamily === 'arial' ||
    normalizedFamily === 'helvetica'
  ) {
    if (isBold && isItalic) {
      return '/fonts/Arial-BoldItalic.ttf';
    }

    if (isBold) {
      return '/fonts/Arial-Bold.ttf';
    }

    if (isItalic) {
      return '/fonts/Arial-Italic.ttf';
    }

    return '/fonts/Arial.ttf';
  }

  if (normalizedFamily === 'verdana') {
    if (isBold && isItalic) return '/fonts/Verdana-BoldItalic.ttf';
    if (isBold) return '/fonts/Verdana-Bold.ttf';
    if (isItalic) return '/fonts/Verdana-Italic.ttf';
    return '/fonts/Verdana.ttf';
  }

  if (
    normalizedFamily === 'times new roman' ||
    normalizedFamily === 'times'
  ) {
    if (isBold && isItalic) return '/fonts/TimesNewRoman-BoldItalic.ttf';
    if (isBold) return '/fonts/TimesNewRoman-Bold.ttf';
    if (isItalic) return '/fonts/TimesNewRoman-Italic.ttf';
    return '/fonts/TimesNewRoman.ttf';
  }

  if (
    normalizedFamily === 'courier new' ||
    normalizedFamily === 'courier'
  ) {
    if (isBold && isItalic) return '/fonts/CourierNew-BoldItalic.ttf';
    if (isBold) return '/fonts/CourierNew-Bold.ttf';
    if (isItalic) return '/fonts/CourierNew-Italic.ttf';
    return '/fonts/CourierNew.ttf';
  }

  if (isBold && isItalic) {
    return '/fonts/Roboto-BoldItalic.ttf';
  }

  if (isBold) {
    return '/fonts/Roboto-Bold.ttf';
  }

  if (isItalic) {
    return '/fonts/Roboto-Italic.ttf';
  }

  return '/fonts/Roboto-Regular.ttf';
}

export async function exportSvgByColor(
  items,
  options = {}
) {
  const {
    flattenToleranceMm = null,
  } = options;

  const fontCache = {};

  const loadFont = async (
    fontFamily,
    fontWeight,
    fontStyle,
    isItalicData
  ) => {
    const path = getFontPath(
      fontFamily,
      fontWeight,
      fontStyle,
      isItalicData
    );

    if (fontCache[path]) {
      return fontCache[path];
    }

    console.log(`Loading font from path: ${path}`);

    const fontPromise = fetch(path)
      .then(async (response) => {
        if (!response.ok) {
          console.warn(`Font file not found at ${path}, falling back to Roboto-Regular.ttf`);
          const fallbackPath = '/fonts/Roboto-Regular.ttf';

          if (path === fallbackPath) {
            throw new Error(`Fallback font not found: ${fallbackPath}`);
          }

          const fallbackResponse = await fetch(fallbackPath);
          if (!fallbackResponse.ok) {
            throw new Error(`Font not found and fallback failed: ${fallbackPath}`);
          }
          return fallbackResponse.arrayBuffer();
        }
        return response.arrayBuffer();
      })
      .then((buffer) => {
        return opentype.parse(buffer, {});
      })
      .catch((error) => {
        delete fontCache[path];

        console.warn(
          `Could not load font ${path}, falling back to Roboto-Regular`,
          error
        );

        if (
          path === '/fonts/Roboto-Regular.ttf'
        ) {
          return null;
        }

        return loadFont(
          'Roboto',
          'normal',
          'normal',
          false
        );
      });

    fontCache[path] = fontPromise;

    return fontPromise;
  };


  if (!items || items.length === 0) {
    throw new Error(
      "Немає об'єктів для експорту"
    );
  }

  const leafSources = [];

  items.forEach((item) => {
    collectLeafItems(
      item,
      leafSources
    );
  });

  if (leafSources.length === 0) {
    throw new Error(
      "Немає видимих обʼєктів для експорту"
    );
  }

  const originalActiveLayer =
    paper.project.activeLayer;

  const tempLayer = new paper.Layer({
    insert: true,
  });

  tempLayer.visible = false;

  const clones = [];
  const cloneColorGroups = new Map();

  leafSources.forEach((source) => {
    const clone = source.clone({
      insert: false,
      deep: true,
    });

    tempLayer.addChild(clone);
    clones.push(clone);

    const sourceColor = getSourceColor(source);
    const matched = matchBaseLaserColor(sourceColor);
    cloneColorGroups.set(clone, matched);
  });

  try {

    let bounds = null;

    clones.forEach((clone) => {
      const itemBounds =
        clone.geometricBounds ||
        clone.bounds;

      if (!itemBounds) {
        return;
      }

      bounds = bounds
        ? bounds.unite(itemBounds)
        : itemBounds.clone();
    });

    if (!bounds) {
      throw new Error(
        "Не вдалося визначити bounds об'єктів"
      );
    }


    const widthMm = pxToMm(
      bounds.width
    );

    const heightMm = pxToMm(
      bounds.height
    );

    const halfWidthMm =
      widthMm / 2;

    const halfHeightMm =
      heightMm / 2;

    const combinedElements = [];

    const vectorGroups = new Map();
    BASE_LASER_COLORS.forEach((c) => vectorGroups.set(c.name, []));

    const elementPromises =
      clones.map(async (clone) => {
        const colorGroup = cloneColorGroups.get(clone) || BASE_LASER_COLORS[0];
        const fillHex = colorGroup.hex;

        let workItem = clone;

        if (
          clone instanceof paper.Shape
        ) {
          workItem =
            clone.toPath(false);

          tempLayer.addChild(
            workItem
          );

          clone.remove();
        }

        if (
          clone instanceof paper.PointText
        ) {
          const fontFamily = clone.data?.fontFamily || clone.fontFamily;
          const fontWeight = clone.fontWeight;
          const fontStyle = clone.fontStyle;
          const isItalic = clone.data?.isItalic;

          const itemFont =
            await loadFont(
              fontFamily,
              fontWeight,
              fontStyle,
              isItalic
            );

          if (
            itemFont &&
            clone.content
          ) {
            const fontSize =
              clone.fontSize || 30;

            const lines =
              clone.content.split('\n');

            const lineHeight =
              fontSize * 1.2;

            const textGroup =
              new paper.Group();

            const clonedMatrix = clone.matrix.clone();
            const fontPath = getFontPath(fontFamily, fontWeight, fontStyle, isItalic);
            if (fontPath.toLowerCase().includes('italic')) {
                if (isItalic) {
                    clonedMatrix.shear(-0.2, 0);
                }
            }

            for (
              let i = 0;
              i < lines.length;
              i++
            ) {
              const line = lines[i];

              console.log(`Processing line ${i}: "${line}"`);

              if (!line) {
                continue;
              }

              let xOffset = 0;

              if (
                clone.justification ===
                  'center' ||
                clone.justification ===
                  'right'
              ) {
                const textWidth =
                  itemFont.getAdvanceWidth(
                    line,
                    fontSize
                  );

                if (
                  clone.justification ===
                  'center'
                ) {
                  xOffset =
                    -textWidth / 2;
                } else {
                  xOffset =
                    -textWidth;
                }
              }

              const pathData =
                itemFont
                  .getPath(
                    line,
                    xOffset,
                    i * lineHeight,
                    fontSize,
                    { kerning: true }
                  )
                  .toPathData(5);

              if (!pathData) {
                continue;
              }


              const linePath =
                paper.project.importSVG(
                  `<path d="${pathData}" fill="#000000"/>`
                );

              if (linePath) {
                const processPath = (p) => {
                  if (p instanceof paper.Path) {
                    if (p.segments.length > 1) {
                      const first = p.firstSegment.point;
                      const last = p.lastSegment.point;
                      if (first.getDistance(last) < 0.05) {
                        p.closed = true;
                      }
                    }
                  } else if (p.children) {
                    p.children.forEach(processPath);
                  }
                };
                processPath(linePath);
              }

              console.log(`linePath imported: ${!!linePath}`);

              if (!linePath) {
                continue;
              }

              textGroup.addChild(
                linePath
              );
              if (
                clone.data?.underline
              ) {
                const textWidth =
                  itemFont.getAdvanceWidth(
                    line,
                    fontSize
                  );

                const underlineThickness =
                  fontSize * 0.05;

                const underlineY =
                  i * lineHeight +
                  fontSize * 0.15;

                const underlinePath =
                  new paper.Path.Rectangle({
                    point: [xOffset, underlineY],
                    size: [textWidth, underlineThickness],
                  });

                textGroup.addChild(
                  underlinePath
                );
              }
            }

            workItem =
              textGroup;

            workItem.matrix = clonedMatrix;

            tempLayer.addChild(workItem);

            clone.remove();
          } else {
            clone.remove();

            return;
          }
        }

        bakeTransform(
          workItem
        );

        flattenIfNeeded(
          workItem,
          flattenToleranceMm
        );

        workItem.translate(
          bounds.center.negate()
        );


        workItem.scale(
          MM_PER_UNIT,
          new paper.Point(0, 0)
        );


        const vectorBucket = vectorGroups.get(colorGroup.name);

        if (
          isSimpleTriangle(
            workItem
          )
        ) {
          combinedElements.push(
            pathToPolygon(workItem, { mode: 'fill', fillHex })
          );
          vectorBucket.push(
            pathToPolygon(workItem, { mode: 'vector' })
          );

          return;
        }

        const svgFill = pathToSvg(workItem, { mode: 'fill', fillHex });
        const svgVector = pathToSvg(workItem, { mode: 'vector' });

        if (svgFill) {
          combinedElements.push(svgFill);
        }
        if (svgVector) {
          vectorBucket.push(svgVector);
        }
      });

    await Promise.all(
      elementPromises
    );

    if (combinedElements.length === 0) {
      throw new Error(
        "Не вдалося сформувати SVG"
      );
    }


    const buildSvgDocument = (elements) => `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg"
     width="${formatNumber(widthMm, 4)}mm"
     height="${formatNumber(heightMm, 4)}mm"
     viewBox="${formatNumber(-halfWidthMm, 4)} ${formatNumber(-halfHeightMm, 4)} ${formatNumber(widthMm, 4)} ${formatNumber(heightMm, 4)}">
${elements.join('\n')}
</svg>`;

    const files = [];

    files.push({
      name: 'combined',
      hex: null,
      svg: buildSvgDocument(combinedElements),
      itemCount: combinedElements.length,
    });

    BASE_LASER_COLORS.forEach((colorInfo) => {
      const elements = vectorGroups.get(colorInfo.name);
      if (!elements || elements.length === 0) return;

      files.push({
        name: colorInfo.name,
        hex: colorInfo.hex,
        svg: buildSvgDocument(elements),
        itemCount: elements.length,
      });
    });

    return files;
  } finally {
    tempLayer.remove();
    if (
      originalActiveLayer &&
      originalActiveLayer.activate
    ) {
      originalActiveLayer.activate();
    }
  }
}